import {
  APPEARANCE_ATTRIBUTE,
  MOTION,
  NOTICE_ROOT_ATTRIBUTE,
  NOTICE_THEME_VARS,
} from "../shared/appearance";
import {
  CONTROL_ATTRIBUTE,
  DOCK_ATTRIBUTE,
  DOCKED,
  GLASS_ROW_CLASS,
  GLASS_STYLE_ID,
  GLASS_VARS,
  LAYOUT_STYLE_ID,
  MOTION_ATTRIBUTE,
  MOTION_OFF,
  NOTICE_KEYFRAMES,
  SWITCH_CONTROL,
  SWITCH_SIZES,
  SWITCH_STYLE_ID,
  SWITCH_THUMB_INSET,
} from "./constants";

/**
 * Adds a stylesheet to a root once: the document's head, or the shadow root a
 * surface renders in (a document stylesheet never reaches into a shadow tree).
 */
export const injectStyleSheet = (
  root: Document | ShadowRoot,
  id: string,
  css: string,
): void => {
  if (typeof document === "undefined") return;
  const existing =
    root instanceof Document ? root.getElementById(id) : root.querySelector(`#${id}`);
  if (existing) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = css;
  (root instanceof Document ? root.head : root).appendChild(style);
};

const N = `[${NOTICE_ROOT_ATTRIBUTE}]`;

const FLOAT_FRAMES = `
    from { opacity: 0; transform: translate(-50%, calc(-50% + 16px)) scale(0.98); }
    to { opacity: 1; transform: translate(-50%, -50%); }`;
const FLOAT_FRAMES_STILL = `
    from { opacity: 0; transform: translate(-50%, -50%); }
    to { opacity: 1; transform: translate(-50%, -50%); }`;

/** Declares the float frames under both float names. */
const floatKeyframes = (frames: string) =>
  [NOTICE_KEYFRAMES.float, NOTICE_KEYFRAMES.floatPill]
    .map((name) => `@keyframes ${name} {${frames}\n  }`)
    .join("\n");

/**
 * What every layout needs beyond inline styles: the entrance keyframes (a
 * docked sheet rises, a corner card settles, a centred glass card floats), the
 * dock's dynamic viewport height, and the console's motion switch.
 */
export const layoutCss = `
  /* dvh tracks the phone's collapsing toolbars; vh stays inline as the
     fallback for browsers without it. */
  @supports (height: 100dvh) {
    ${N}[${DOCK_ATTRIBUTE}="${DOCKED}"] {
      max-height: 92dvh !important;
    }
  }

  @keyframes ${NOTICE_KEYFRAMES.rise} {
    from { transform: translateY(104%); }
    to { transform: none; }
  }

  /* Ends on the translate the card is centred with inline. */
  ${floatKeyframes(FLOAT_FRAMES)}

  @keyframes ${NOTICE_KEYFRAMES.fade} {
    from { opacity: 0; }
  }

  @keyframes ${NOTICE_KEYFRAMES.settle} {
    from { opacity: 0; transform: translateY(12px); }
  }

  @keyframes ${NOTICE_KEYFRAMES.pop} {
    from { opacity: 0; transform: scale(0.94); }
  }

  @keyframes ${NOTICE_KEYFRAMES.spin} {
    to { transform: rotate(360deg); }
  }

  /* Motion switched off in the console: every entrance and transition stops,
     the spinner included, since a still ring still reads as loading. */
  ${N}[${MOTION_ATTRIBUTE}="${MOTION_OFF}"],
  ${N}[${MOTION_ATTRIBUTE}="${MOTION_OFF}"] * {
    animation: none !important;
    transition: none !important;
  }

  @media (prefers-reduced-motion: reduce) {
    @keyframes ${NOTICE_KEYFRAMES.rise} {
      from { opacity: 0; }
    }
    ${floatKeyframes(FLOAT_FRAMES_STILL)}
    @keyframes ${NOTICE_KEYFRAMES.settle} {
      from { opacity: 0; }
    }
    @keyframes ${NOTICE_KEYFRAMES.pop} {
      from { opacity: 0; }
    }
    ${N} *,
    ${N} *::after {
      transition-duration: 0ms !important;
    }
  }
`;

const G = `[${NOTICE_ROOT_ATTRIBUTE}][${APPEARANCE_ATTRIBUTE}="glass"]`;

/**
 * Every custom property carries a neutral fallback, so a rule that reaches an
 * element outside a root that sets them still draws something visible.
 */
const GLASS_FALLBACKS: Readonly<Record<string, string>> = {
  [GLASS_VARS.accent]: "currentColor",
  [GLASS_VARS.accentMid]: "rgba(128, 128, 128, 0.55)",
  [GLASS_VARS.accentFaint]: "rgba(128, 128, 128, 0.06)",
  [GLASS_VARS.track]: "rgba(128, 128, 128, 0.3)",
  [GLASS_VARS.outline]: "rgba(128, 128, 128, 0.4)",
  [GLASS_VARS.hairline]: "rgba(128, 128, 128, 0.15)",
  [GLASS_VARS.control]: "rgba(255, 255, 255, 0.72)",
};

const v = (name: string) => `var(${name}, ${GLASS_FALLBACKS[name]})`;

/** Glass drawing native checkboxes, not switches (the switch sheet owns those). */
const C = `${G}:not([${CONTROL_ATTRIBUTE}="${SWITCH_CONTROL}"])`;

/**
 * The glass notice's stylesheet: what inline styles cannot express (focus
 * rings, hairlines, press feedback). Every rule is scoped under the appearance
 * attribute so classic never matches one, and every colour is a custom
 * property the notice root sets from the workspace brand.
 */
export const glassCss = `
  /* One focus ring in the brand accent, for keyboard users only. The base
     sheet rings checkboxes on any :focus, which a tap would show too. */
  ${G} .redacto-checkbox-large:focus:not(:focus-visible),
  ${G} .redacto-checkbox-medium:focus:not(:focus-visible),
  ${G} .redacto-checkbox-small:focus:not(:focus-visible) {
    outline: none !important;
  }

  ${G} .redacto-checkbox-large:focus-visible,
  ${G} .redacto-checkbox-medium:focus-visible,
  ${G} .redacto-checkbox-small:focus-visible,
  ${G} button:focus-visible,
  ${G} a:focus-visible,
  ${G} select:focus-visible,
  ${G} input:focus-visible,
  ${G} [role="button"]:focus-visible,
  ${G} [role="option"]:focus-visible {
    outline: 2px solid ${v(GLASS_VARS.accent)} !important;
    outline-offset: 2px !important;
  }

  /* Checkboxes on a translucent sheet: classic's pale grey edge all but
     vanishes, so an unticked box takes a brand-tinted edge on a light fill.
     An admin's toggle-off colour still wins. */
  ${C} .redacto-checkbox-large:not(:checked),
  ${C} .redacto-checkbox-medium:not(:checked),
  ${C} .redacto-checkbox-small:not(:checked) {
    border-color: var(${NOTICE_THEME_VARS.controlOff}, ${v(GLASS_VARS.accentMid)}) !important;
    background-color: ${v(GLASS_VARS.control)} !important;
  }

  /* Hairlines between the rows of a grouped list. */
  ${G} .${GLASS_ROW_CLASS} + .${GLASS_ROW_CLASS} {
    border-top: 1px solid ${v(GLASS_VARS.hairline)};
  }

  /* Tactile press feedback. */
  ${G} button {
    transition: transform 180ms ${MOTION.outEase}, filter 200ms, opacity 200ms;
    -webkit-tap-highlight-color: transparent;
  }

  ${G} button:not(:disabled):active {
    transform: scale(0.97);
  }

  ${G} button:not(:disabled):hover {
    filter: brightness(1.05);
  }

  ${G} [role="option"]:not([aria-selected="true"]):hover {
    background-color: ${v(GLASS_VARS.accentFaint)} !important;
  }

  @media (prefers-reduced-motion: reduce) {
    ${G} button {
      transition: none !important;
    }
  }
`;

/**
 * Purpose and product checkboxes drawn as switches, for any style: the admin
 * picks switches in the console (or the host in `settings.controlStyle`); no
 * style draws them by default. The inputs stay native checkboxes, so keyboard,
 * form and assistive-tech behaviour are the checkbox's own.
 *
 * Colours read the glass palette's properties first (set only under glass),
 * then the console palette's, then the classic defaults.
 */
const S = `[${NOTICE_ROOT_ATTRIBUTE}][${CONTROL_ATTRIBUTE}="${SWITCH_CONTROL}"]`;

const ACCENT = `var(${GLASS_VARS.accent}, var(${NOTICE_THEME_VARS.accent}, #4f87ff))`;
const TRACK = `var(${GLASS_VARS.track}, var(${NOTICE_THEME_VARS.controlOff}, #d0d5dd))`;
const KNOB = `var(${NOTICE_THEME_VARS.knob}, #ffffff)`;
const OUTLINE = `var(${GLASS_VARS.outline}, var(${NOTICE_THEME_VARS.controlOff}, #d0d5dd))`;
const CHECK_FILL = `var(${GLASS_VARS.control}, #ffffff)`;

const switchRules = (size: keyof typeof SWITCH_SIZES) => {
  const { width, height, thumb } = SWITCH_SIZES[size];
  const travel = width - thumb - SWITCH_THUMB_INSET * 2;
  const sel = `${S} .redacto-checkbox-${size}`;
  return `
    ${sel} {
      width: ${width}px !important;
      height: ${height}px !important;
      border: 0 !important;
      border-radius: 999px !important;
      background-color: ${TRACK} !important;
      padding: 0 !important;
      transition: background-color 280ms ${MOTION.outEase} !important;
    }

    ${sel}::after,
    ${sel}:checked::after {
      content: '' !important;
      position: absolute !important;
      top: ${SWITCH_THUMB_INSET}px !important;
      left: ${SWITCH_THUMB_INSET}px !important;
      width: ${thumb}px !important;
      height: ${thumb}px !important;
      border: 0 !important;
      border-radius: 999px !important;
      background: ${KNOB} !important;
      box-shadow: 0 2px 6px rgba(15, 23, 42, 0.25) !important;
      box-sizing: border-box !important;
      transform: none !important;
      transition: transform 340ms ${MOTION.sheetEase} !important;
    }

    ${sel}:checked {
      background-color: ${ACCENT} !important;
    }

    ${sel}:checked::after {
      transform: translateX(${travel}px) !important;
    }

    /* No mid-state rule here on purpose: a switch has two positions. */
  `;
};

export const switchCss = `
  ${switchRules("large")}
  ${switchRules("medium")}

  /* Data elements: a round check, the switch's companion. */
  ${S} .redacto-checkbox-small {
    width: 20px !important;
    height: 20px !important;
    border: 1.5px solid ${OUTLINE} !important;
    border-radius: 999px !important;
    background-color: ${CHECK_FILL} !important;
    padding: 0 !important;
    transition: background-color 220ms ${MOTION.outEase},
      border-color 220ms ${MOTION.outEase} !important;
  }

  ${S} .redacto-checkbox-small:checked {
    background-color: ${ACCENT} !important;
    border-color: ${ACCENT} !important;
  }

  ${S} .redacto-checkbox-small:checked::after {
    top: 45% !important;
    width: 4px !important;
    height: 8px !important;
    border-width: 0 2px 2px 0 !important;
  }

  ${S} .redacto-checkbox-large:disabled,
  ${S} .redacto-checkbox-medium:disabled,
  ${S} .redacto-checkbox-small:disabled {
    opacity: 0.55 !important;
    cursor: default !important;
  }

  /* The console's motion switch, off; the layout sheet's own rule is weaker
     than these, so it cannot stop the switches itself. */
  ${S}[${MOTION_ATTRIBUTE}="${MOTION_OFF}"] .redacto-checkbox-large,
  ${S}[${MOTION_ATTRIBUTE}="${MOTION_OFF}"] .redacto-checkbox-large::after,
  ${S}[${MOTION_ATTRIBUTE}="${MOTION_OFF}"] .redacto-checkbox-medium,
  ${S}[${MOTION_ATTRIBUTE}="${MOTION_OFF}"] .redacto-checkbox-medium::after,
  ${S}[${MOTION_ATTRIBUTE}="${MOTION_OFF}"] .redacto-checkbox-small {
    transition: none !important;
  }

  @media (prefers-reduced-motion: reduce) {
    ${S} .redacto-checkbox-large,
    ${S} .redacto-checkbox-large::after,
    ${S} .redacto-checkbox-medium,
    ${S} .redacto-checkbox-medium::after,
    ${S} .redacto-checkbox-small {
      transition: none !important;
    }
  }
`;

/** The keyframes and motion switch every layout uses; idempotent per root. */
export const injectLayoutStyles = (root: Document | ShadowRoot): void =>
  injectStyleSheet(root, LAYOUT_STYLE_ID, layoutCss);

/** Injected on first glass render only, so a notice that never asks for glass carries none. */
export const injectGlassStyles = (root: Document | ShadowRoot): void =>
  injectStyleSheet(root, GLASS_STYLE_ID, glassCss);

/** Injected the first time a notice draws switches; idempotent per root. */
export const injectSwitchStyles = (root: Document | ShadowRoot): void =>
  injectStyleSheet(root, SWITCH_STYLE_ID, switchCss);
